import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, CreditCard } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API, CartContext } from '../App';
import { toAbsoluteImageUrl } from '../utils/image';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const CheckoutForm = ({ formData, setFormData, shippingRates, selectedShipping, setSelectedShipping, cart, subtotal, shippingCost, total, orderCompleted, setOrderCompleted }) => {
  const navigate = useNavigate();
  const { clearCart } = useContext(CartContext);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (value) => {
    setFormData({
      ...formData,
      country: value
    });
  };

  const validateForm = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
    for (let field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Create order directly (payment will be completed via QR codes)
      const orderData = {
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        items: cart.map(item => ({
          productId: item.id,
          productName: item.name,
          productImage: toAbsoluteImageUrl(item.images[0]) || 'https://via.placeholder.com/120x120?text=No+Image',
          quantity: item.quantity,
          price: item.price
        })),
        subtotal,
        shipping: shippingCost,
        total,
        shippingAddress: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        }
      };

      const response = await axios.post(`${API}/orders`, orderData);

      toast.success('Order created! Please scan the QR code to complete payment.');
      setOrderCompleted(true); // Set flag before clearing cart

      // Persist order for the success page in case of navigation or refresh
      localStorage.setItem('lastOrder', JSON.stringify(response.data));

      // Navigate to success page with order details to show QR codes
      navigate('/checkout/success', { state: { order: response.data } });
      
      // Clear cart after navigation is initiated
      setTimeout(() => {
        clearCart();
      }, 100);
      
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Error creating order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form" data-testid="checkout-form">
      <div className="form-section">
        <h2 className="form-section-title">Contact Information</h2>
        
        <div className="form-row">
          <div className="form-group">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              data-testid="input-firstName"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              data-testid="input-lastName"
            />
          </div>
        </div>

        <div className="form-group">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            data-testid="input-email"
          />
        </div>

        <div className="form-group">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            data-testid="input-phone"
          />
        </div>
      </div>

      <div className="form-section">
        <h2 className="form-section-title">Shipping Address</h2>
        
        <div className="form-group">
          <Label htmlFor="address">Street Address *</Label>
          <Input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            data-testid="input-address"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
              data-testid="input-city"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="state">State *</Label>
            <Input
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              data-testid="input-state"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <Label htmlFor="zipCode">Zip Code *</Label>
            <Input
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              required
              data-testid="input-zipCode"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="country">Country *</Label>
            <Select value={formData.country} onValueChange={handleCountryChange}>
              <SelectTrigger data-testid="select-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
                <SelectItem value="UK">United Kingdom</SelectItem>
                <SelectItem value="DE">Germany</SelectItem>
                <SelectItem value="FR">France</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {shippingRates && (
        <div className="form-section">
          <h2 className="form-section-title">Shipping Method</h2>
          <div className="shipping-options" data-testid="shipping-options">
            <label className="shipping-option">
              <input
                type="radio"
                name="shipping"
                value="standard"
                checked={selectedShipping === 'standard'}
                onChange={(e) => setSelectedShipping(e.target.value)}
                data-testid="shipping-standard"
              />
              <div>
                <span className="shipping-name">Standard Shipping</span>
                <span className="shipping-price">${shippingRates.standard.toFixed(2)}</span>
              </div>
              <span className="shipping-time">5-7 business days</span>
            </label>
            
            <label className="shipping-option">
              <input
                type="radio"
                name="shipping"
                value="express"
                checked={selectedShipping === 'express'}
                onChange={(e) => setSelectedShipping(e.target.value)}
                data-testid="shipping-express"
              />
              <div>
                <span className="shipping-name">Express Shipping</span>
                <span className="shipping-price">${shippingRates.express.toFixed(2)}</span>
              </div>
              <span className="shipping-time">2-3 business days</span>
            </label>
          </div>
        </div>
      )}

      <div className="form-section payment-info-section">
        <h2 className="form-section-title">Payment Method</h2>
        <div className="payment-methods-notice">
          <CreditCard size={24} />
          <div>
            <p className="notice-title">Pay with Cash App, Venmo, or Zelle</p>
            <p className="notice-description">
              After placing your order, you'll receive QR codes to complete your payment via Cash App, Venmo, or Zelle.
              Your order will be processed once payment is received.
            </p>
          </div>
        </div>
      </div>

      <Button 
        type="submit"
        disabled={loading}
        className="place-order-btn"
        data-testid="place-order-button"
      >
        <Lock size={18} />
        {loading ? 'Creating Order...' : `Place Order - $${total.toFixed(2)}`}
      </Button>
    </form>
  );
};

const Checkout = () => {
  const { cart } = useContext(CartContext);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });
  
  const [shippingRates, setShippingRates] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [orderCompleted, setOrderCompleted] = useState(false);

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shippingCost = shippingRates ? shippingRates[selectedShipping] : 0;
  const total = subtotal + shippingCost;

  useEffect(() => {
    if (cart.length === 0 && !orderCompleted) {
      navigate('/cart');
      return;
    }
  }, [cart, navigate, orderCompleted]);

  useEffect(() => {
    fetchShippingRates();
  }, [formData.country]);

  const fetchShippingRates = async () => {
    try {
      const response = await axios.get(`${API}/shipping/rates?country=${formData.country}`);
      setShippingRates(response.data);
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      toast.error('Error loading shipping rates');
    }
  };

  return (
    <div data-testid="checkout-page">
      <Navbar />
      
      <div className="checkout-container">
        <h1 className="checkout-title" data-testid="checkout-title">Checkout</h1>

        <div className="checkout-layout">
          <CheckoutForm 
            formData={formData}
            setFormData={setFormData}
            shippingRates={shippingRates}
            selectedShipping={selectedShipping}
            setSelectedShipping={setSelectedShipping}
            cart={cart}
            subtotal={subtotal}
            shippingCost={shippingCost}
            total={total}
            orderCompleted={orderCompleted}
            setOrderCompleted={setOrderCompleted}
          />

          {/* Order Summary */}
          <div className="order-summary" data-testid="order-summary">
            <h2 className="summary-title">Order Summary</h2>
            
            <div className="summary-items">
              {cart.map((item) => (
                <div key={item.id} className="summary-item" data-testid={`summary-item-${item.id}`}>
                  <img src={toAbsoluteImageUrl(item.images[0]) || 'https://via.placeholder.com/120x120?text=No+Image'} alt={item.name} className="summary-item-image" />
                  <div className="summary-item-details">
                    <p className="summary-item-name">{item.name}</p>
                    <p className="summary-item-quantity">Qty: {item.quantity}</p>
                  </div>
                  <p className="summary-item-price">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="summary-calculations">
              <div className="summary-row">
                <span>Subtotal</span>
                <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping</span>
                <span data-testid="summary-shipping">${shippingCost.toFixed(2)}</span>
              </div>
              <div className="summary-total">
                <span>Total</span>
                <span data-testid="summary-total">${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="secure-notice">
              <Lock size={16} />
              <span>Secure payment via Cash App or Venmo</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        .checkout-container {
          margin-top: 80px;
          padding: 3rem 2rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          min-height: calc(100vh - 200px);
        }

        .checkout-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 2rem;
        }

        .loading-checkout {
          text-align: center;
          padding: 4rem;
          font-size: 1.2rem;
          color: #718096;
        }

        .checkout-layout {
          display: grid;
          grid-template-columns: 1fr 450px;
          gap: 3rem;
        }

        .checkout-form {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .form-section {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .form-section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .shipping-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .shipping-option {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1.5rem;
          border: 2px solid #E2E8F0;
          border-radius: 10px;
          cursor: pointer;
          transition: border-color 0.3s ease;
        }

        .shipping-option:has(input:checked) {
          border-color: #FF9B9B;
          background: rgba(255, 155, 155, 0.05);
        }

        .shipping-option input {
          margin-right: 0.75rem;
        }

        .shipping-option > div {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
        }

        .shipping-name {
          font-size: 1rem;
          color: #2D3748;
        }

        .shipping-price {
          color: #FF9B9B;
          font-size: 1.1rem;
        }

        .shipping-time {
          font-size: 0.9rem;
          color: #718096;
          margin-left: 1.75rem;
        }

        .payment-info-section {
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.05) 0%, rgba(180, 167, 255, 0.05) 100%);
          border: 2px solid #E2E8F0;
        }

        .payment-methods-notice {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          background: white;
          border-radius: 10px;
          border-left: 4px solid #FF9B9B;
        }

        .payment-methods-notice svg {
          color: #FF9B9B;
          flex-shrink: 0;
          margin-top: 0.25rem;
        }

        .notice-title {
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.5rem;
          font-size: 1.05rem;
        }

        .notice-description {
          color: #718096;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .order-summary {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          height: fit-content;
          position: sticky;
          top: 100px;
        }

        .summary-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 1.5rem;
          font-family: 'Playfair Display', serif;
        }

        .summary-items {
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid #F7FAFC;
        }

        .summary-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .summary-item-image {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }

        .summary-item-details {
          flex: 1;
        }

        .summary-item-name {
          font-weight: 600;
          color: #2D3748;
          margin-bottom: 0.25rem;
          font-size: 0.9rem;
        }

        .summary-item-quantity {
          font-size: 0.85rem;
          color: #718096;
        }

        .summary-item-price {
          font-weight: 700;
          color: #2D3748;
        }

        .summary-calculations {
          margin-bottom: 2rem;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.75rem;
          color: #4A5568;
        }

        .summary-total {
          display: flex;
          justify-content: space-between;
          font-size: 1.3rem;
          font-weight: 700;
          color: #2D3748;
          padding-top: 1rem;
          margin-top: 1rem;
          border-top: 2px solid #F7FAFC;
          margin-bottom: 1.5rem;
        }

        .secure-notice {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #F7FAFC;
          border-radius: 8px;
          color: #718096;
          font-size: 0.85rem;
        }

        .place-order-btn {
          width: 100%;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1.2rem;
          border-radius: 50px;
          border: none;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .place-order-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .place-order-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 1024px) {
          .checkout-container {
            margin-top: 60px;
            padding: 2rem 1rem;
          }

          .checkout-layout {
            grid-template-columns: 1fr;
          }

          .order-summary {
            position: static;
          }

          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Checkout;
